import { Link, router } from "@inertiajs/react"
import {
  BadgeCheck,
  BellRing,
  Check,
  ExternalLink,
  RefreshCw,
  Send,
  UserCheck,
  X,
} from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import {
  RejectionReasonPicker,
  isNoteMissing,
} from "@/components/candidates/rejection-reason-picker"
import { InviteCopyButton } from "@/components/invites/invite-copy-button"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { useIsMobile } from "@/hooks/use-mobile"
import { useOnline } from "@/hooks/use-online"
import { useRejectionReasonOptionsFor } from "@/hooks/use-rejection-reason-options"
import { cn } from "@/lib/utils"
import type { CandidateReviewActions as Actions } from "@/types/home-triage"

/**
 * OKS-706 — Contextual inline action row for a Candidates-to-review card.
 *
 * Single visibility rule (mirror `candidate-action-bar`): each button
 * renders IFF its server-supplied action URL is non-null — no
 * disabled-with-tooltip path. Reading order (UX Spec §2): Open (ghost,
 * leftmost) · Refuse (destructive/corrective) · forward-primary
 * (Review / Approve / Approve & copy invite, rightmost).
 *
 * Wire format matches the OKS-19 transition surface — every direct POST
 * sends the nested `{transition: {event}}` params. All posts
 * `preserveScroll` and land back on `/brand` (referer-aware redirect), so
 * the section reloads in place; the count badge decrements as cards leave
 * the `new`+`reviewed` set.
 *
 * OKS-948 — the **Approve & copy invite** one-flow is REMOVED. It minted an
 * invite link before the candidate had been validated or contacted, which
 * epic OKS-947 ruling A1 forbids. The card keeps Open · Refuse · Approve;
 * the path to a link is Approve, then generate it from the candidate's
 * Invitations section. No replacement button is added.
 *
 * Offline guard: `useOnline` disables every mutating button while offline.
 */
export interface CandidateReviewActionsProps {
  actions: Actions
  /** `@handle` — for the Refuse confirmation accessible name + copy. */
  handle: string
  /**
   * **OKS-952** — the card's `from_status`, i.e. what a refusal here would be
   * recorded against. Selects this candidate's slice of the server-sent
   * taxonomy.
   *
   * Reaches `reviewed` and `pending_brand_validation` in practice: every card
   * serializer emits `reject_url` only at those two. It is nonetheless read
   * from `item.status` rather than assumed, because
   * `brand_home#awaiting_validation_card_actions` gates on `card_permits?`
   * (legal-from ALL five statuses) and is confined to
   * `pending_brand_validation` only by its section's QUERY SCOPE — a scope, not
   * a check. Widen that query and the affordance follows silently; keying off
   * the real status means the picker follows correctly instead of offering a
   * stale gate's options.
   */
  status?: string | null
  /** Active invite URL for surfaced approved/invited cards (gesture-safe copy). */
  inviteUrl?: string | null
  /**
   * **OKS-950** — when this candidate was last escalated to the brand (ISO
   * 8601), else null.
   *
   * Renders the muted "already nudged" line in the button's place while
   * `actions.escalate_url` is null because of the 7-day cooloff. Without it
   * the button would just vanish, and an agency member could not tell whether
   * their nudge landed or the affordance never existed at all — the same
   * absence-is-ambiguous problem `appeal_state` solves on the detail page.
   */
  escalatedAt?: string | null
  className?: string
}

export function CandidateReviewActions({
  actions,
  handle,
  inviteUrl = null,
  escalatedAt = null,
  status = null,
  className,
}: CandidateReviewActionsProps) {
  const { t } = useTranslation()
  const online = useOnline()
  const isMobile = useIsMobile()
  const [submitting, setSubmitting] = useState(false)
  const [rejectOpen, setRejectOpen] = useState(false)
  const [rejectReason, setRejectReason] = useState("")
  // OKS-952 — coded reason + the blocked-submit flag (raised on submit only,
  // never on selection).
  const [rejectReasonCode, setRejectReasonCode] = useState("")
  const [rejectNoteMissing, setRejectNoteMissing] = useState(false)
  const rejectOptions = useRejectionReasonOptionsFor(status)

  const disabled = submitting || !online

  function postTransition(url: string, event: string) {
    if (disabled) return
    setSubmitting(true)
    router.post(
      url,
      { transition: { event } },
      { preserveScroll: true, onFinish: () => setSubmitting(false) },
    )
  }

  /**
   * OKS-950 — the escalate POST. Deliberately NOT routed through
   * `postTransition`: escalation is not a status transition, so it carries no
   * `{transition: {event}}` body. Sending one would imply a state change the
   * endpoint does not make — and ⛔ the whole point of this affordance is that
   * a waiting candidate is never moved by the passage of time.
   */
  function postEscalate(url: string) {
    if (disabled) return
    setSubmitting(true)
    router.post(
      url,
      {},
      { preserveScroll: true, onFinish: () => setSubmitting(false) },
    )
  }

  function submitReject() {
    const url = actions.reject_url
    if (!url || disabled) return
    // OKS-952 — latency-only; the server re-checks and is authoritative.
    if (isNoteMissing(rejectReasonCode, rejectReason)) {
      setRejectNoteMissing(true)
      return
    }
    setRejectNoteMissing(false)
    setSubmitting(true)
    const trimmed = rejectReason.trim()
    const transition = {
      event: "reject",
      ...(trimmed.length > 0 ? { reason: rejectReason } : {}),
      ...(rejectReasonCode ? { reason_code: rejectReasonCode } : {}),
    }
    router.post(
      url,
      { transition },
      {
        preserveScroll: true,
        onSuccess: () => {
          setRejectOpen(false)
          setRejectReason("")
          setRejectReasonCode("")
          setRejectNoteMissing(false)
        },
        onFinish: () => setSubmitting(false),
      },
    )
  }

  function handleRejectOpenChange(open: boolean) {
    setRejectOpen(open)
    // Clear the whole buffer, code included — a stale code is invisible in a
    // collapsed select and would be silently applied to the next candidate.
    if (!open) {
      setRejectReason("")
      setRejectReasonCode("")
      setRejectNoteMissing(false)
    }
  }

  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      {/* Open — ghost, leftmost. */}
      <Button
        asChild
        type="button"
        variant="ghost"
        size="sm"
        data-testid="candidate-review-open"
      >
        <Link
          href={actions.open_url}
          aria-label={t("candidates.home_review.actions.open_aria", { handle })}
        >
          <ExternalLink className="size-4" aria-hidden="true" />
          {t("candidates.home_review.actions.open")}
        </Link>
      </Button>

      {/* Refuse — corrective, opens confirmation with optional reason. */}
      {actions.reject_url && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          aria-disabled={disabled}
          onClick={() => setRejectOpen(true)}
          data-testid="candidate-review-reject"
        >
          <X className="size-4" aria-hidden="true" />
          {t("candidates.home_review.actions.reject")}
        </Button>
      )}

      {/* Review — forward-primary (`new`). */}
      {actions.review_url && (
        <Button
          type="button"
          size="sm"
          disabled={disabled}
          aria-disabled={disabled}
          onClick={() => postTransition(actions.review_url!, "mark_reviewed")}
          data-testid="candidate-review-mark-reviewed"
        >
          <UserCheck className="size-4" aria-hidden="true" />
          {t("candidates.home_review.actions.review")}
        </Button>
      )}

      {/* Approve — forward-primary (`reviewed`). */}
      {actions.approve_url && (
        <Button
          type="button"
          size="sm"
          disabled={disabled}
          aria-disabled={disabled}
          onClick={() => postTransition(actions.approve_url!, "approve")}
          data-testid="candidate-review-approve"
        >
          <Check className="size-4" aria-hidden="true" />
          {t("candidates.home_review.actions.approve")}
        </Button>
      )}

      {/*
        OKS-950 — Validate. The brand's sign-off, and the only forward-primary
        on a brand-validation card (nothing competes with it at that status,
        unlike `approve` which OKS-673 relocated into the Invitations section).

        `aria-label` carries the LONG form ("Validate @handle for the brand")
        while the button reads just "Valider": the section title supplies the
        context visually, but a screen-reader user hearing a bare "Validate"
        next to the review card's "Approve" cannot tell them apart. The long
        form wraps at 375px, which is why it is not the visible label (OQ10).
      */}
      {actions.validate_url && (
        <Button
          type="button"
          size="sm"
          disabled={disabled}
          aria-disabled={disabled}
          aria-label={t("candidates.home_review.actions.validate_aria", {
            handle,
          })}
          onClick={() => postTransition(actions.validate_url!, "validate")}
          data-testid="candidate-review-validate"
        >
          <BadgeCheck className="size-4" aria-hidden="true" />
          {t("candidates.home_review.actions.validate")}
        </Button>
      )}

      {/*
        OKS-950 — the agency's day-14 nudge, and its cooloff line.

        Exactly one of these renders. A live URL means the re-ping is
        available; otherwise a timestamp means it was used recently and the
        7-day cooloff is running. Both null means the candidate simply has not
        waited long enough, and nothing renders — that case needs no
        explanation because there is nothing to explain yet.

        Confirm-free by design (UX Spec §4): it sends a notification and
        changes no status, so a confirmation would cost more than the action.
      */}
      {actions.escalate_url ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          aria-disabled={disabled}
          onClick={() => postEscalate(actions.escalate_url!)}
          data-testid="candidate-review-escalate"
        >
          <BellRing className="size-4" aria-hidden="true" />
          {t("candidates.home_review.actions.escalate")}
        </Button>
      ) : (
        escalatedAt && (
          <span
            data-testid="candidate-review-escalated-recently"
            className="text-muted-foreground text-xs"
          >
            {t("candidates.home_review.actions.escalated_recently")}
          </span>
        )
      )}

      {/*
        OKS-951 — « Relancer » on the follow-up section.

        A LINK, never a POST. Logging an attempt straight from the card is
        deliberately not offered (UX Spec §4): the point of a follow-up is to
        read the thread first, and the panel is where the pitch, the channel and
        the prior attempts live. Deep-links with `?focus=contact` so the panel is
        scrolled to and focused on arrival.

        Emitted only by the « À relancer » consumer, so it is DOM-absent on the
        three siblings — `follow_up_url` is optional and undefined there, which
        behaves identically to null under the hide-when-null rule.
      */}
      {actions.follow_up_url && (
        <Button
          type="button"
          variant="default"
          size="sm"
          asChild
          data-testid="candidate-review-follow-up"
          aria-label={
            status === "approved"
              ? t("candidates.home_to_contact.actions.contact_aria", { handle })
              : undefined
          }
        >
          {/*
            OKS-953 — the SAME affordance serves segments 3 and 4: a link into
            the contact panel. Only the verb differs, and `status` is the
            honest discriminator — an `approved` candidate has never been
            reached, so the button says « Contacter »; a `contacted` one says
            « Relancer ».

            Deriving it here rather than adding a label prop keeps the two
            sections from having to know about each other, and keeps the
            `follow_up_url` contract single. Without this, segment 3 rendered
            « Relancer » at someone nobody had contacted yet.
          */}
          <Link href={actions.follow_up_url}>
            <Send className="size-4" aria-hidden="true" />
            {status === "approved"
              ? t("candidates.home_to_contact.actions.contact")
              : t("candidates.home_follow_up.actions.follow_up")}
          </Link>
        </Button>
      )}

      {/*
        OKS-953 — « Régénérer le lien ». A POST, unlike its neighbours.

        ⚠️ The server emits this URL ONLY once the link is already dead, which
        is why it ships with no confirmation dialog: regeneration kills the
        token the creator holds, and offering it on a LIVE link would silently
        break a working invite. The gate is the state, not a modal — so do NOT
        "improve" this by rendering it unconditionally with an AlertDialog.
      */}
      {actions.regenerate_url && (
        <Button
          type="button"
          variant="default"
          size="sm"
          data-testid="candidate-review-regenerate-invite"
          aria-label={t(
            "candidates.home_invite_stale.actions.regenerate_aria",
            { handle },
          )}
          onClick={() =>
            router.post(
              actions.regenerate_url!,
              {},
              {
                preserveScroll: true,
                onSuccess: () =>
                  toast.success(
                    t("candidates.home_invite_stale.toast.regenerated"),
                  ),
              },
            )
          }
        >
          <RefreshCw className="size-4" aria-hidden="true" />
          {t("candidates.home_invite_stale.actions.regenerate")}
        </Button>
      )}

      {/* Surfaced approved/invited card with an active invite → copy it. */}
      {inviteUrl && (
        <InviteCopyButton
          url={inviteUrl}
          variant="outline"
          testId="candidate-review-copy-invite"
        />
      )}

      {isMobile ? (
        <Drawer open={rejectOpen} onOpenChange={handleRejectOpenChange}>
          <DrawerContent>
            <DrawerHeader>
              <DrawerTitle>
                {t("candidates.home_review.reject.title", { handle })}
              </DrawerTitle>
              <DrawerDescription>
                {t("candidates.home_review.reject.description")}
              </DrawerDescription>
            </DrawerHeader>
            <div className="px-4 pb-2">
              <RejectionReasonPicker
                options={rejectOptions}
                value={rejectReasonCode}
                onValueChange={setRejectReasonCode}
                note={rejectReason}
                onNoteChange={setRejectReason}
                namePrefix="transition"
                idPrefix="candidate-review-reject"
                noteTestId="candidate-review-reject-reason"
                noteMissing={rejectNoteMissing}
                disabled={submitting}
              />
            </div>
            <DrawerFooter>
              <Button
                type="button"
                variant="destructive"
                onClick={submitReject}
                disabled={disabled}
                data-testid="candidate-review-reject-confirm"
              >
                {t("candidates.home_review.reject.confirm")}
              </Button>
              <DrawerClose
                disabled={submitting}
                render={<Button type="button" variant="ghost" />}
              >
                {t("candidates.home_review.reject.cancel")}
              </DrawerClose>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      ) : (
        <AlertDialog open={rejectOpen} onOpenChange={handleRejectOpenChange}>
          <AlertDialogContent data-testid="candidate-review-reject-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("candidates.home_review.reject.title", { handle })}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t("candidates.home_review.reject.description")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <RejectionReasonPicker
              options={rejectOptions}
              value={rejectReasonCode}
              onValueChange={setRejectReasonCode}
              note={rejectReason}
              onNoteChange={setRejectReason}
              namePrefix="transition"
              idPrefix="candidate-review-reject"
              noteTestId="candidate-review-reject-reason"
              noteMissing={rejectNoteMissing}
              disabled={submitting}
            />
            <AlertDialogFooter>
              <AlertDialogCancel disabled={submitting}>
                {t("candidates.home_review.reject.cancel")}
              </AlertDialogCancel>
              {/* OKS-1023 — plain `Button`, NOT `AlertDialogAction`: the Action
                  is Base UI `Dialog.Close` and would close the dialog over a
                  refused (`isNoteMissing`) or in-flight submit. The dialog
                  closes in `submitReject`'s `onSuccess`. */}
              <Button
                type="button"
                data-testid="candidate-review-reject-confirm"
                disabled={disabled}
                className="bg-destructive hover:bg-destructive/90 text-white"
                onClick={submitReject}
              >
                {t("candidates.home_review.reject.confirm")}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  )
}
